import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, Images, Videos, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES, STATUS_IDENTIFIERS } from "models";
import { useAppointmentLocation, useQuestionResultMutation } from "modules/AppointmentDetails/hooks";

import type { Appointment, QuestionResult, QuestionResultImage, QuestionResultVideo } from "models";

type QuestionResultMediaProps = {
  canUpdate: boolean;
  appointment: Appointment;
  questionResult: QuestionResult;
};

export const QuestionResultMedia = ({ canUpdate, appointment, questionResult }: QuestionResultMediaProps) => {
  const appointmentLocation = useAppointmentLocation(appointment);
  const { t } = useTranslation();
  const canUploadVideo = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.CarChecks) && canUpdate;

  const [selectedDeleteImage, setSelectedDeleteImage] = useState<QuestionResultImage | null>(null);
  const [selectedDeleteVideo, setSelectedDeleteVideo] = useState<QuestionResultVideo | null>(null);

  const { mutate: updateQuestionResult, isPending: isUpdateQuestionResultPending } = useQuestionResultMutation();

  const images = questionResult.images?.filter(item => item.active) || [];
  const videos = questionResult.videos?.filter(item => item.active) || [];

  const updateImage = (updatedImage: QuestionResultImage) => {
    updateQuestionResult({
      ...questionResult,
      images: questionResult.images?.map(img => (img.id === updatedImage.id ? updatedImage : img))
    });
  };

  const handleDeleteImage = (image: QuestionResultImage) => updateImage({ ...image, active: false });
  const handleImageVisibility = (image: QuestionResultImage) => updateImage({ ...image, visible_to_customer: !image.visible_to_customer });

  const updateVideo = (updatedVideo: QuestionResultVideo) => {
    updateQuestionResult({
      ...questionResult,
      videos: questionResult.videos?.map(video => (video.id === updatedVideo.id ? updatedVideo : video))
    });
  };

  const handleDeleteVideo = (video: QuestionResultVideo) => updateVideo({ ...video, active: false });
  const handleVideoUpload = (video: QuestionResultVideo, url: string) => updateVideo({ ...video, url });
  const handleVideoVisibility = (video: QuestionResultVideo) => updateVideo({ ...video, visible_to_customer: !video.visible_to_customer });

  const handleCancelDeleteMedia = () => {
    setSelectedDeleteImage(null);
    setSelectedDeleteVideo(null);
  };

  const handleConfirmDeleteMedia = () => {
    if (selectedDeleteImage) handleDeleteImage(selectedDeleteImage);
    if (selectedDeleteVideo) handleDeleteVideo(selectedDeleteVideo);

    handleCancelDeleteMedia();
  };

  return (
    <>
      <div className="checklist-media">
        <Images images={images} onDeleteRequest={setSelectedDeleteImage} canDelete={canUpdate} onChangeVisibility={handleImageVisibility} />
        <Videos
          appointment={appointment}
          location={appointmentLocation}
          videos={videos}
          isEditable={appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus && canUpdate}
          handleDeleteVideo={setSelectedDeleteVideo}
          handleVideoUpload={canUploadVideo ? handleVideoUpload : undefined}
          handleVideoVisibility={handleVideoVisibility}
        />
      </div>
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        handleCancel={handleCancelDeleteMedia}
        isOpen={selectedDeleteImage !== null || selectedDeleteVideo !== null}
        isLoading={isUpdateQuestionResultPending}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleConfirmDeleteMedia}
      />
    </>
  );
};
